import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useVoiceCallSession } from "../../hooks/useVoiceCallSession";
import type { VoiceCallUi, VoiceCallSession } from "../../lib/webrtcCall";
import { useContacts } from "./ContactsContext";
import { useRealtime } from "./RealtimeContext";
import { useAuth } from "./AuthContext";

type CallContextValue = {
  voiceUi: VoiceCallUi;
  setVoiceUi: React.Dispatch<React.SetStateAction<VoiceCallUi>>;
  voiceSessionRef: React.MutableRefObject<VoiceCallSession | null>;
  remoteAudioRef: React.MutableRefObject<HTMLAudioElement | null>;
  startVoiceCall: (peerId: string | null) => void;
};

const CallContext = createContext<CallContextValue | null>(null);

export function CallProvider({ children }: { children: ReactNode }) {
  const { keys } = useAuth();
  const { contactsState } = useContacts();
  const { isWsLeader, sendRealtimeCommand } = useRealtime();

  const { voiceUi, setVoiceUi, voiceSessionRef, remoteAudioRef, startVoiceCall } = useVoiceCallSession({
    keysUserId: keys?.userId ?? null,
    isWsLeader,
    contacts: contactsState.contacts,
    sendRealtimeCommand,
  });

  const value = useMemo(
    () => ({
      voiceUi,
      setVoiceUi,
      voiceSessionRef,
      remoteAudioRef,
      startVoiceCall,
    }),
    [voiceUi, setVoiceUi, startVoiceCall],
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCall(): CallContextValue {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error("useCall outside CallProvider");
  return ctx;
}
